<template>
  <div class="locations" :class="cssClass">
    <h3
        class="margin-0"
        :class="{
        'text-danger': store.error && store.requireFields?.includes('locations'),
      }"
        >
      <span class="locations-title">
        {{ t('measurement-location').toUpperCase() }}
      </span>

      <div
          v-if="cssClass !== 'vms' && sortedLocations.length"
          class="select-all-container"
      >
        <span class="select-all-text">
          {{ allSelected ? t('unmark') : t('mark') }} {{ t('all').toLowerCase() }}
        </span>

            <input-checkbox
                field-name="selectAll"
                field-value=""
                :checkedCondition="allSelected"
                @clicked="setAllFilter()"
            />
      </div>
    </h3>

    <table class="table">
      <thead>
        <tr>
          <th class="sign" scope="col">></th>
          <th scope="col">Id</th>
          <th scope="col">{{ t('location-name') }}</th>
          <th scope="col" class="path-number">{{ t('road-no') }}</th>
          <th scope="col">{{ t('chainage') }}</th>
          <th scope="col">{{ t('select') }}</th>
        </tr>
      </thead>

      <tbody v-if="sortedLocations.length">
        <tr v-for="loc in sortedLocations" :key="loc.id">
          <td scope="row">
            <img :src="isActive(loc) ? availableIcon : unavailableIcon" alt="Status" />
          </td>
          <td>{{ loc.id }}</td>
          <td>{{ loc.shortName }}</td>
          <td>{{ loc.roadNumber }}</td>
          <td>{{ loc.chainage }}</td>
          <td class="check">
            <input-checkbox
              :field-name="String(loc.id)"
              field-value=""
              :checkedCondition="isChecked(loc.id)"
              @clicked="setFilter(loc.id)"
            />
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="6">
            <p class="no-data center-aligned">{{ t('no-data') }}</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import InputCheckbox from "../InputCheckbox.vue";
import availableIcon from "@/assets/icons/dostepny.png";
import unavailableIcon from "@/assets/icons/niedostepny.png";

export default {
  name: "LocationList",
  components: { InputCheckbox },
  props: {
    store: { required: true },
    cssClass: String,
    freshnessThresholdMinutes: { type: Number, default: 30 },
    lastTimeIsUnixSeconds: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,
      tick: 0,           // 👈 reaktywne „tyknięcie” czasu
      timer: null,
      availableIcon,
      unavailableIcon,
    };
  },
  created() {
    // po wejściu (np. po zalogowaniu) od razu pobierasz lokalizacje
    this.store.getLocations?.();
  },
  mounted() {
    // timer zostaje – ale służy tylko jako trigger do przerysowania
    this.timer = setInterval(() => {
      this.tick++;       // zmiana tick => Vue przeliczy template
    }, 60 * 1000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  watch: {
    "store.locations"(newVal) {
      this.loading = !newVal || newVal.length === 0;
    },
  },
  computed: {
    sortedLocations() {
      const arr = Array.isArray(this.store.locations) ? this.store.locations : [];
      return [...arr].sort((a, b) => Number(a.id) - Number(b.id));
    },
    selectedIdSet() {
      const ids = this.store?.filters?.locations || [];
      return new Set(ids.map((x) => Number(x)));
    },
    allSelected() {
      const locs = this.sortedLocations;
      if (!locs.length) return false;
      return locs.every((l) => this.selectedIdSet.has(Number(l.id)));
    },
    thresholdMs() {
      return this.freshnessThresholdMinutes * 60 * 1000;
    },
  },
  methods: {
    setFilter(id) {
      this.store.addItem?.("LocationList", Number(id));
    },
    isChecked(id) {
      return this.selectedIdSet.has(Number(id));
    },
    setAllFilter() {
      this.store.checkAllLocations?.();
    },
    parseTimestamp(value) {
      if (value == null) return NaN;
      if (typeof value === "number") {
        return this.lastTimeIsUnixSeconds ? value * 1000 : value;
      }
      const trimmed = String(value).trim();
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return this.lastTimeIsUnixSeconds ? n * 1000 : n;
      }
      const ms = Date.parse(trimmed);
      return Number.isNaN(ms) ? NaN : ms;
    },
    isActive(loc) {
      // 👇 to jest *hak na reactivity* – metoda zależy od tick,
      // więc gdy timer zmieni tick, Vue przerysuje wiersz
      this.tick;

      const ts = this.parseTimestamp(loc?.lastVehicleTime);
      if (Number.isNaN(ts)) return false;
      const now = Date.now(); // zawsze aktualny czas
      return now - ts <= this.thresholdMs;
    },
  },
};
</script>

<style scoped>
.locations {
  //width: 100%;
}

/* NAGŁÓWEK */
.locations h3 {
  padding: 0.85rem 1rem 0.9rem 1rem;
  width: 100%;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
  border-top: 1px solid #bfbfbf;

  display: flex;          /* tytuł + "zaznacz wszystkie" + checkbox w jednej linii */
  align-items: center;    /* pionowe wyśrodkowanie */
}

.locations-title {
  margin-right: 1rem;
}

/* tekst "zaznacz wszystkie" + checkbox po PRAWEJ stronie nagłówka */
.select-all-container {
  margin-left: auto;      /* wypchnij cały blok na prawo */
  display: flex;
  align-items: center;
  gap: 0.5rem;            /* odstęp między tekstem a checkboxem */
}

.select-all-text {
  font-size: 12px;
}

/* TABELA */
.locations table {
  width: 100%;
  border-collapse: collapse;
}

.locations th,
.locations td {
  text-align: center;
  vertical-align: middle;
  font-weight: normal;
  font-size: 12px;
  padding: 0.75rem;
}

/* obramowania wierszy */
.locations tr {
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
}

.locations td.check span {
  margin: 0 auto;
  margin-top: 0.1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem;
}

/* kolumna z ">" */
.sign {
  color: #eaeaea;
  font-weight: 1000;
  font-size: 15px;
}

/* numer drogi, żeby się nie łamał */
.path-number {
  white-space: nowrap;
}
</style>
